import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TruckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { getLinkedClientsFromTruck, getAssignedDriverDisplayLines } from '../utils/helpers';

/** Distinct chip styles so each value is easy to scan (cycles if many items). */
const CHIP_STYLES = [
  'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/70',
  'bg-violet-100 text-violet-900 ring-1 ring-violet-200/70',
  'bg-amber-100 text-amber-900 ring-1 ring-amber-200/70',
  'bg-sky-100 text-sky-900 ring-1 ring-sky-200/70',
  'bg-rose-100 text-rose-900 ring-1 ring-rose-200/70',
  'bg-indigo-100 text-indigo-900 ring-1 ring-indigo-200/70',
  'bg-teal-100 text-teal-900 ring-1 ring-teal-200/70',
  'bg-orange-100 text-orange-900 ring-1 ring-orange-200/70',
];

function ValueChips({ labels, paletteStart = 0 }) {
  if (!labels?.length) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 align-middle">
      {labels.map((label, i) => (
        <span
          key={`${label}-${i}`}
          className={`inline-block max-w-full truncate rounded-md px-2 py-0.5 text-xs font-medium shadow-sm ${CHIP_STYLES[(paletteStart + i) % CHIP_STYLES.length]}`}
          title={label}
        >
          {label}
        </span>
      ))}
    </span>
  );
}

const DriverMyTrucksPage = () => {
  const { t } = useTranslation();
  const { getDriverTrucks } = useAuth();
  const trucks = getDriverTrucks();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('driverTrucks.title')}</h1>
        <p className="mt-1 text-gray-600">{t('driverTrucks.subtitle')}</p>
      </div>

      {trucks.length === 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t('driverTrucks.empty')}
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {trucks.map((truck) => {
            const clients = getLinkedClientsFromTruck(truck);
            const clientLabels = clients.map((c) => c.companyName).filter(Boolean);
            const assignedDriverLines = getAssignedDriverDisplayLines(truck);
            return (
              <li
                key={truck.id}
                className="flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-md bg-blue-50 p-2">
                    <TruckIcon className="h-6 w-6 text-blue-700" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {t('driverTrucks.plate')}
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {truck.plateNumber || truck.id}
                    </p>
                    <div className="mt-2 space-y-1">
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        {t('driverTrucks.assignedDrivers')}
                      </div>
                      {assignedDriverLines.length > 0 ? (
                        <ValueChips labels={assignedDriverLines} />
                      ) : (
                        <span className="text-sm italic text-gray-500">{t('driverTrucks.noAssignedDrivers')}</span>
                      )}
                    </div>
                    {clientLabels.length > 0 ? (
                      <div className="mt-3 space-y-1">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          {t('driverTrucks.clients')}
                        </div>
                        <ValueChips labels={clientLabels} paletteStart={3} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div>
        <Link
          to="/loads"
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700"
        >
          {t('driverTrucks.goToLoads')}
          <ArrowRightIcon className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </div>
  );
};

export default DriverMyTrucksPage;
